import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/fonts.css";
import "./index.css";
import "./styles/home.css";
import { RouterProvider } from "react-router";
import { router } from "./controller/Routes";
import { GlobalProvider } from "./controller/GlobalState";
import { HeadProvider } from "react-head";
import { initGA } from "./controller/analytics";

initGA();

const app = (
  <StrictMode>
    <HeadProvider>
      <GlobalProvider>
        <RouterProvider router={router} />
      </GlobalProvider>
    </HeadProvider>
  </StrictMode>
);

const container = document.getElementById("root");

if (container.childElementCount === 0) {
  // Nothing prerendered (admin, blog posts, preview): render as a normal SPA.
  createRoot(container).render(app);
} else {
  bootOverPrerender(container, app);
}

/**
 * Take over a prerendered page without the layout jumping.
 *
 * Every route except Home is code-split. Rendering straight into #root threw
 * away the prerendered markup, showed the Suspense <PageLoader /> while the
 * route chunk downloaded, then showed the page again — so the footer jumped
 * up and back down. That single shift measured 0.40 CLS (four times the
 * "poor" threshold) on every lazy route, and made the prerendered hero vanish
 * and fade back in.
 *
 * Instead, render into an invisible sibling and leave the prerendered markup
 * on screen until the real route has mounted and its above-the-fold reveal
 * animations have finished, then swap them. Both render the same layout, so
 * the swap moves nothing.
 *
 * The staging element is visibility:hidden rather than display:none or
 * clipped, because framer-motion's whileInView uses IntersectionObserver,
 * which still sees geometry under visibility:hidden — so the hero animates in
 * while hidden and is already at rest when it appears.
 */
function bootOverPrerender(prerendered, tree) {
  const staging = document.createElement("div");
  staging.setAttribute("aria-hidden", "true");
  staging.style.cssText = "position:absolute;top:0;left:0;right:0;visibility:hidden;pointer-events:none;";
  prerendered.after(staging);
  createRoot(staging).render(tree);

  let swapped = false;
  let settling = false;

  const swap = () => {
    if (swapped) return;
    swapped = true;
    observer.disconnect();
    clearTimeout(fallback);
    staging.removeAttribute("aria-hidden");
    staging.removeAttribute("style");
    prerendered.replaceWith(staging);
    staging.id = "root";
  };

  const settleThenSwap = async () => {
    if (settling) return;
    settling = true;
    // framer-motion starts a reveal from its IntersectionObserver callback, a
    // beat after mount, so reading getAnimations() immediately would find
    // nothing and swap mid-fade. Give the reveals time to start, then wait for
    // whatever is running. Capped so a looping animation can never hold the
    // swap back.
    const cap = new Promise((r) => setTimeout(r, 1500));
    const settled = (async () => {
      await new Promise((r) => setTimeout(r, 250));
      const running = staging.getAnimations({ subtree: true }).map((a) => a.finished.catch(() => {}));
      await Promise.all(running);
    })();
    await Promise.race([settled, cap]);
    requestAnimationFrame(swap);
  };

  const ready = () => staging.querySelector("h1, h2") && !staging.querySelector(".page-loader");

  const observer = new MutationObserver(() => {
    if (ready()) settleThenSwap();
  });
  observer.observe(staging, { childList: true, subtree: true });
  if (ready()) settleThenSwap();

  // Never leave a visitor on the static snapshot if something upstream stalls.
  const fallback = setTimeout(swap, 6000);
}
